import {
    combineTransactionSteps,
    findChildrenInRange,
    getChangedRanges,
    mergeAttributes,
} from "@tiptap/core";
import Mention, { type MentionOptions as MentionOptionsOriginal } from "@tiptap/extension-mention";
import { Plugin, type Transaction } from "@tiptap/pm/state";
import { type Editor, type Node, ReactNodeViewRenderer, ReactRenderer } from "@tiptap/react";
import Suggestion, { type SuggestionProps } from "@tiptap/suggestion";
import { type ComponentProps } from "react";
import { v4 as uuid4 } from "uuid";

import { type DiscussionsLinkedTypes } from "types/enum";

import { MentionUserSelectOverlay } from "commonComponents/btWrappers/editor/core/BTEditorContent/overlays/MentionUserSelectOverlay/MentionUserSelectOverlay";
import { MentionUserNodeView } from "commonComponents/btWrappers/editor/core/extensions/custom/mentionUser/MentionUserNodeView";
import { type IMentionUserExtensionConfig } from "commonComponents/btWrappers/editor/core/extensions/ExtensionProvider.types";
import { type TiptapExtensionStorageType } from "commonComponents/btWrappers/editor/editor.types";

export const MentionUserExtensionChar = "@";

export enum MentionUserTypes {
    User = 0,
    Everyone = 1,
    // Future types could be Role, Job, Company, etc.
}

export interface IUserMentionSuggestionUser {
    name: string;
    globalUserId: number;
    mentionUserType: MentionUserTypes.User;
}

export interface IUserMentionSuggestionEveryone {
    mentionUserType: MentionUserTypes.Everyone;

    /**
     * The total number of users that will be notified when @Everyone is mentioned. Not required if it is
     * unknown
     */
    count?: number;
}

export type UserMentionSuggestion = IUserMentionSuggestionUser | IUserMentionSuggestionEveryone;

declare module "@tiptap/core" {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- third-party interface augmentation
    interface Storage {
        mentionUser?: IMentionUserStorage;
    }
}

export interface IMentionUserAttributes {
    id: number;
    label: string;
    mentionId: string;
    /**
     * The type of the user mention, usually just "user", but can be "everyone" for \@Everyone mentions.
     *
     * @default MentionUserTypes.User
     * @see MentionUserTypes
     */
    mentionUserType?: MentionUserTypes;
}

export interface IMentionUserStorage {
    // The current users being suggested in the dropdown. This value is set in the MentionUserSelectOverlay,
    // and used in the suggestion utility to determine if the suggestion should be shown.
    suggestedUsers: UserMentionSuggestion[];
}

export interface IMentionOptions
    extends Omit<MentionOptionsOriginal<UserMentionSuggestion>, "suggestion"> {
    // Overrides default type. For some reason the mention extension doesn't expose the editor object to the suggestion function.
    // This changes that behavior to allow the suggestion function to access the editor object.
    suggestion: (editor: Editor) => MentionOptionsOriginal<UserMentionSuggestion>["suggestion"];

    /**
     * When false, the mention extension will still be enabled, but not show any searchable users in the dropdown,
     * and instead show a message explaining why it's disabled.
     *
     * @default false so make sure to set true that is confirmed to fully support mention notifications.
     */
    unsupportedEntityType?: DiscussionsLinkedTypes;
}

export const MentionUserExtensionName = "mentionUser";
export const MentionUserExtension = (
    Mention as unknown as Node<IMentionOptions, TiptapExtensionStorageType<typeof Mention>>
).extend({
    name: MentionUserExtensionName,
    selectable: true,
    atom: true,
    draggable: true,

    addStorage() {
        return {
            suggestedUsers: [] satisfies UserMentionSuggestion[],
        } as IMentionUserStorage;
    },

    addOptions() {
        const parentOptions = this.parent?.();
        // `IMentionOptions.suggestion` is a function-of-editor (so suggestion code can capture
        // the editor instance); wrap the parent's plain object to match.
        const parentSuggestion = parentOptions?.suggestion;
        return {
            ...parentOptions,
            HTMLAttributes: parentOptions?.HTMLAttributes ?? {},
            renderText:
                parentOptions?.renderText ??
                (({ node }) => `${MentionUserExtensionChar}${node.attrs.label}`),
            renderHTML:
                parentOptions?.renderHTML ??
                (({ node }) => [
                    "span",
                    { "data-type": "mentionUser" },
                    `${MentionUserExtensionChar}${node.attrs.label}`,
                ]),
            deleteTriggerWithBackspace: parentOptions?.deleteTriggerWithBackspace ?? false,
            suggestions: parentOptions?.suggestions ?? [],
            suggestion: parentSuggestion ? () => parentSuggestion : () => ({}),
            unsupportedEntityType: undefined,
        };
    },

    addAttributes() {
        return {
            // id and label are copied from the original mention extension. The internals of the extension use these attributes, so don't change their names:
            // https://github.com/ueberdosis/tiptap/blob/main/packages/extension-mention/src/mention.ts
            ["id" satisfies keyof IMentionUserAttributes]: {
                default: null,
                parseHTML: (element) => Number(element.getAttribute("data-id")),
                renderHTML: (attributes) => {
                    if (!attributes.id) {
                        return {};
                    }

                    return {
                        "data-id": attributes.id,
                    };
                },
            },

            ["label" satisfies keyof IMentionUserAttributes]: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-label"),
                renderHTML: (attributes) => {
                    if (!attributes.label) {
                        return {};
                    }

                    return {
                        "data-label": attributes.label,
                    };
                },
            },

            /** A GUID generated on the frontend that is unique per mention node across the app. Is used to generate notifications in the backend. */
            ["mentionId" satisfies keyof IMentionUserAttributes]: {
                default: null,
                keepOnSplit: false,
                rendered: false,
            },

            ["mentionUserType" satisfies keyof IMentionUserAttributes]: {
                default: MentionUserTypes.User,
                parseHTML: (element) => {
                    const type = element.getAttribute("data-mention-type");
                    return type ? Number(type) : MentionUserTypes.User;
                },
                renderHTML: (attributes) => {
                    if (attributes.mentionUserType === undefined) {
                        return {};
                    }

                    return {
                        "data-mention-type": attributes.mentionUserType,
                    };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: "span[data-type=mentionUser]",
                getAttrs: () => {
                    // For now, don't allow pasting of this node into the editor.
                    // There are concerns that the user might not have access to the entity.
                    // This will be revisited in the future.
                    return false;
                },
            },
        ];
    },
    renderText({ node }: { node: Editor["state"]["doc"] }) {
        const attributes = node.attrs as IMentionUserAttributes;
        return `${MentionUserExtensionChar}${attributes.label}`;
    },
    renderHTML({ HTMLAttributes, node }) {
        return [
            "span",
            mergeAttributes(
                { "data-type": this.name },
                this.options.HTMLAttributes,
                HTMLAttributes,
            ),
            `${MentionUserExtensionChar}${node.attrs.label}`,
        ];
    },
    addNodeView() {
        return ReactNodeViewRenderer(MentionUserNodeView, {
            as: "span",
        });
    },
    addProseMirrorPlugins() {
        // This hackyness is used to extend the suggestion to only show the mention popover when the input
        // to trigger the suggestion happened from a character insertion/deletion, AKA the editor content is different.
        // This prevents the popover from showing when the cursor is moved back and forth.
        //
        // This check can't be put inside the "allow" function because we can't use any methods provided by TipTap's
        // suggestion plugin because it doesn't pass enough data to distinguish between a character insertion/deletion
        // and a transaction that doesn't change the editor content.
        const originalSuggestion = Suggestion({
            editor: this.editor,
            ...this.options.suggestion(this.editor),
        });
        return [
            new Plugin({
                ...originalSuggestion.spec,
                state: originalSuggestion.spec.state && {
                    ...originalSuggestion.spec.state,
                    apply(tr, value, oldState, newState) {
                        if (!tr.docChanged || !originalSuggestion.spec.state?.apply) {
                            return value;
                        }

                        return originalSuggestion.spec.state.apply(tr, value, oldState, newState);
                    },
                },
            }),
            // This plugin is used to generate a unique mentionId for each mention node
            // as nodes cannnot default an attribute with a non-static value. Taken from
            // https://github.com/MoltenCoffee/tiptap-unique-id/tree/main
            new Plugin({
                appendTransaction: (transactions: readonly Transaction[], oldState, newState) => {
                    // Check if there are any document changes
                    if (!transactions.some((t) => t.docChanged) || oldState.doc.eq(newState.doc)) {
                        return;
                    }
                    // Only process changed ranges instead of the entire document
                    const tr: Transaction = newState.tr;
                    const transform = combineTransactionSteps(oldState.doc, [...transactions]);
                    const changedRanges = getChangedRanges(transform);
                    changedRanges.forEach(({ newRange }) => {
                        // Find mention nodes that need IDs only in the changed ranges
                        const newNodes = findChildrenInRange(
                            newState.doc,
                            newRange,
                            (node) =>
                                node.isAtom &&
                                !node.attrs.mentionId &&
                                node.type.name === this.name,
                        );
                        // Apply mentionId to each node that needs it
                        newNodes.forEach(({ pos }) => {
                            tr.setNodeAttribute(pos, "mentionId", uuid4());
                        });
                    });
                    // Only return the transaction if it contains changes
                    return tr.steps.length ? tr : undefined;
                },
            }),
        ];
    },
});

export interface IUserSuggestionOptions {
    searchUsersStaticRef?: (query: string) => Promise<UserMentionSuggestion[]>;
    unsupportedEntityType?: DiscussionsLinkedTypes;
}

export const userSuggestion = (
    config: IMentionUserExtensionConfig,
): IMentionOptions["suggestion"] => {
    return (editor) => ({
        allowSpaces: true,
        char: MentionUserExtensionChar,
        // Note: command is copied from the mention extension, except for one line that sets the mention node type
        command: ({ editor, range, props }) => {
            // increase range.to by one when the next node is of type "text"
            // and starts with a space character
            const nodeAfter = editor.view.state.selection.$to.nodeAfter;
            const overrideSpace = nodeAfter?.text?.startsWith(" ");

            if (overrideSpace) {
                range.to += 1;
            }

            editor
                .chain()
                .focus()
                .insertContentAt(range, [
                    {
                        type: MentionUserExtensionName,
                        attrs: props,
                    },
                    {
                        type: "text",
                        text: " ",
                    },
                ])
                .run();

            // get reference to `window` object from editor element, to support cross-frame JS usage
            editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd();
        },

        allow: ({ editor, state, range }) => {
            const text = state.doc.textBetween(range.from, range.to);
            const users = editor.storage.mentionUser?.suggestedUsers ?? [];
            const numSpaces = (text.match(/ /g) || []).length;

            // Close the mention if:
            // 1. The text starts with "@ " (which is the mention character followed by a space)
            // 2. There are two or more spaces in the text and no users are suggested
            return !text.startsWith("@ ") && (numSpaces < 2 || users.length !== 0);
        },

        items: async ({ query }) => {
            if (!config.mentionUser?.configure?.searchUsersStaticRef) {
                return [];
            }
            return await config.mentionUser.configure.searchUsersStaticRef(query);
        },

        render() {
            let popup: ReactRenderer | null = null;
            const destroyPopup = () => {
                if (popup) {
                    popup.destroy();
                    popup = null;
                }
            };

            const getOnSelectionFinalized =
                (props: SuggestionProps<UserMentionSuggestion>) =>
                (user: UserMentionSuggestion) => {
                    if (user.mentionUserType === MentionUserTypes.Everyone) {
                        props.command({
                            id: -1, // Use a special ID for @Everyone mentions
                            label: "Everyone",
                            mentionUserType: user.mentionUserType,
                        } satisfies Omit<IMentionUserAttributes, "mentionId">);
                    } else {
                        props.command({
                            id: user.globalUserId,
                            label: user.name,
                            mentionUserType: MentionUserTypes.User,
                        } satisfies Omit<IMentionUserAttributes, "mentionId">);
                    }
                    destroyPopup();
                };

            return {
                onStart: (props: SuggestionProps<UserMentionSuggestion>) => {
                    if (!props.clientRect) {
                        return;
                    }
                    popup = new ReactRenderer(MentionUserSelectOverlay, {
                        props: {
                            editor: props.editor,
                            users: props.items,
                            query: props.query,
                            onSelectionFinalized: getOnSelectionFinalized(props),
                            onClose: () => {
                                destroyPopup();
                            },
                            unsupportedEntityType:
                                config.mentionUser?.configure?.unsupportedEntityType,
                        } satisfies ComponentProps<typeof MentionUserSelectOverlay>,
                        editor: props.editor,
                    });

                    document.body.appendChild(popup.element);
                },
                onUpdate(props: SuggestionProps<UserMentionSuggestion>) {
                    popup?.updateProps({
                        editor: props.editor,
                        users: props.items,
                        query: props.query,
                        onSelectionFinalized: getOnSelectionFinalized(props),
                        onClose: () => {
                            destroyPopup();
                        },
                    } satisfies ComponentProps<typeof MentionUserSelectOverlay>);
                },

                onKeyDown(props) {
                    if (!popup || config.mentionUser?.configure?.unsupportedEntityType) {
                        // If the popup is not open, or the mention is unsupported, let the editor handle the keydown event
                        return false;
                    }

                    if (props.event.key === "Escape") {
                        destroyPopup();
                        return true;
                    }

                    if (props.event.key === "ArrowRight" || props.event.key === "ArrowLeft") {
                        destroyPopup();
                        return false;
                    }

                    // If enter is pressed and there are no users, destroy the popup and let the editor handle the event so a newline is created
                    if (props.event.key === "Enter") {
                        const users = editor.storage.mentionUser?.suggestedUsers ?? [];
                        if (users.length === 0) {
                            destroyPopup();
                            return false;
                        } else {
                            return true;
                        }
                    }

                    // Returning true makes the editor ignore the keybind, so any keybinds handled inside the file
                    // MentionUserSelectOverlay.tsx need to be ignored here
                    return (
                        props.event.key === "ArrowUp" ||
                        props.event.key === "ArrowDown" ||
                        props.event.key === "Tab"
                    );
                },

                onExit() {
                    destroyPopup();
                },
            };
        },
    });
};
